// src/components/TimeSignature.tsx
import React, { useEffect, useState } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { CircularButton } from "./CircularButton";
import { useToast } from "../hooks/useToast";

interface TimeSignatureProps {
  beatsPerBar: number;
  noteValue: number;
  onBeatsPerBarChange: (beats: number) => void;
  onNoteValueChange: (noteValue: number) => void;
  controlsLocked?: boolean;
}

// CircularButton is now the shared control button

export const TimeSignature: React.FC<TimeSignatureProps> = ({
  beatsPerBar,
  noteValue,
  onBeatsPerBarChange,
  onNoteValueChange,
  controlsLocked,
}) => {
  const { showInfo, showError } = useToast();
  // State for editing functionality
  const [isEditingBeats, setIsEditingBeats] = useState(false);
  const [isEditingNoteValue, setIsEditingNoteValue] = useState(false);
  const [beatsInputValue, setBeatsInputValue] = useState(
    beatsPerBar.toString()
  );
  const [noteValueInputValue, setNoteValueInputValue] = useState(
    noteValue.toString()
  );

  // Button animation is handled inside CircularButton

  // Update input values when props change
  useEffect(() => {
    if (!isEditingBeats) {
      setBeatsInputValue(beatsPerBar.toString());
    }
  }, [beatsPerBar, isEditingBeats]);

  useEffect(() => {
    if (!isEditingNoteValue) {
      setNoteValueInputValue(noteValue.toString());
    }
  }, [noteValue, isEditingNoteValue]);

  // Validation function for beats per bar
  const validateAndSetBeats = (value: string) => {
    let numValue = parseInt(value);

    // Handle empty or invalid input
    if (isNaN(numValue) || value === "") {
      numValue = beatsPerBar; // Keep current value if input is invalid
    } else {
      // Clamp to valid range (2-16)
      const originalValue = numValue;
      if (numValue < 2) numValue = 2;
      if (numValue > 16) numValue = 16;
      if (numValue !== originalValue) {
        showError("Enter a numerator in the range of 2 to 16");
      }
    }

    onBeatsPerBarChange(numValue);
    setBeatsInputValue(numValue.toString());
    setIsEditingBeats(false);
  };

  // Validation function for note value
  const validateAndSetNoteValue = (value: string) => {
    let numValue = parseInt(value);

    // Handle empty or invalid input
    if (isNaN(numValue) || value === "") {
      numValue = noteValue; // Keep current value if input is invalid
    } else {
      // Snap to closest valid note value (2, 4, 8, 16)
      const originalValue = numValue;
      const validValues = [2, 4, 8, 16];
      let closest = validValues[0];
      let minDiff = Math.abs(numValue - closest);

      for (const validValue of validValues) {
        const diff = Math.abs(numValue - validValue);
        if (diff < minDiff) {
          minDiff = diff;
          closest = validValue;
        }
      }

      numValue = closest;
      if (numValue !== originalValue) {
        showError("Note values of 2, 4, 8 and 16 are available");
      }
    }

    onNoteValueChange(numValue);
    setNoteValueInputValue(numValue.toString());
    setIsEditingNoteValue(false);
  };

  // Handle clicking on beats number to edit
  const handleBeatsClick = () => {
    if (controlsLocked) {
      showInfo(
        "Editing the BPM and time signature is disabled in Custom Song Structure mode."
      );
      return;
    }
    setIsEditingBeats(true);
    setBeatsInputValue(beatsPerBar.toString());
  };

  // Handle clicking on note value number to edit
  const handleNoteValueClick = () => {
    if (controlsLocked) {
      showInfo(
        "Editing the BPM and time signature is disabled in Custom Song Structure mode."
      );
      return;
    }
    setIsEditingNoteValue(true);
    setNoteValueInputValue(noteValue.toString());
  };

  // Handle input focus to select all text
  const handleBeatsInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleNoteValueInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // Handle input changes during editing
  const handleBeatsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow numbers and limit to 2 digits
    if (/^\d{0,2}$/.test(value)) {
      setBeatsInputValue(value);
    }
  };

  const handleNoteValueInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    // Only allow numbers and limit to 2 digits
    if (/^\d{0,2}$/.test(value)) {
      setNoteValueInputValue(value);
    }
  };

  // Handle blur events (when input loses focus)
  const handleBeatsInputBlur = () => {
    validateAndSetBeats(beatsInputValue);
  };

  const handleNoteValueInputBlur = () => {
    validateAndSetNoteValue(noteValueInputValue);
  };

  // Handle key press events
  const handleBeatsInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      validateAndSetBeats(beatsInputValue);
    } else if (e.key === "Escape") {
      setBeatsInputValue(beatsPerBar.toString());
      setIsEditingBeats(false);
    }
  };

  const handleNoteValueInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      validateAndSetNoteValue(noteValueInputValue);
    } else if (e.key === "Escape") {
      setNoteValueInputValue(noteValue.toString());
      setIsEditingNoteValue(false);
    }
  };

  const handleBeatsDecrease = () => {
    if (beatsPerBar > 2) {
      onBeatsPerBarChange(beatsPerBar - 1);
    }
  };

  const handleBeatsIncrease = () => {
    if (beatsPerBar < 16) {
      onBeatsPerBarChange(beatsPerBar + 1);
    }
  };

  const handleNoteValueDecrease = () => {
    if (noteValue === 16) {
      onNoteValueChange(8); // 16th to 8th
    } else if (noteValue === 8) {
      onNoteValueChange(4); // 8th to quarter
    } else if (noteValue === 4) {
      onNoteValueChange(2); // quarter to half
    }
  };

  const handleNoteValueIncrease = () => {
    if (noteValue === 2) {
      onNoteValueChange(4); // half to quarter
    } else if (noteValue === 4) {
      onNoteValueChange(8); // quarter to 8th
    } else if (noteValue === 8) {
      onNoteValueChange(16); // 8th to 16th
    }
  };

  return (
    <div className="time-signature">
      <div
        className="time-signature-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
        }}
      >
        {/* Left side - Two minus buttons stacked vertically */}
        <div
          className={`time-signature-buttons ${
            !controlsLocked ? "visible" : ""
          }`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Top minus button - for beats */}
          <CircularButton
            onClick={handleBeatsDecrease}
            disabled={beatsPerBar <= 2}
            size={44}
            tooltip="Decrease beats per bar"
            tooltipShortcut="Ctrl + ↓"
          >
            {FaMinus({ size: 12 })}
          </CircularButton>

          {/* Bottom minus button - for note value */}
          <CircularButton
            onClick={handleNoteValueDecrease}
            disabled={noteValue <= 2}
            size={44}
            tooltip="Decrease note value"
            tooltipShortcut="Ctrl + Shift + ↓"
          >
            {FaMinus({ size: 12 })}
          </CircularButton>
        </div>

        {/* Center sphere with time signature */}
        <div
          className="time-signature-dial"
          style={{
            borderRadius: "50%",
            background: "var(--overlay-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            backdropFilter: "blur(7px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            transition: "all 0.15s ease-out",
          }}
        >
          {/* Time Signature Display */}
          <div
            className="time-signature-text"
            style={{
              fontWeight: "600",
              color: "var(--text-primary)",
              textAlign: "center",
              userSelect: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              position: "relative",
            }}
          >
            {/* Label */}
            <div
              className="time-signature-label"
              style={{
                color: "var(--text-secondary)",
                userSelect: "none",
                position: "absolute",
                top: "75%",
                left: "50%",
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
              }}
            >
              Time Signature
            </div>

            {/* Beats per bar (top number) */}
            <div
              className="time-signature-text"
              style={{
                fontWeight: "600",
                color: "var(--text-primary)",
                lineHeight: "1",
                position: "absolute",
                top: "35%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              {isEditingBeats ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={beatsInputValue}
                  onChange={handleBeatsInputChange}
                  onBlur={handleBeatsInputBlur}
                  onFocus={handleBeatsInputFocus}
                  onKeyDown={handleBeatsInputKeyDown}
                  autoFocus
                  autoComplete="off"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--text-primary)",
                    fontWeight: "600",
                    textAlign: "center",
                    outline: "none",
                    caretColor: "var(--text-primary)",
                    fontFamily: "inherit",
                  }}
                  className="time-signature-input"
                />
              ) : (
                <span
                  onClick={handleBeatsClick}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease-out",
                    display: "inline-block",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--button-hover-bg)";
                    e.currentTarget.style.borderRadius = "8px";
                    e.currentTarget.style.padding = "2px 6px";
                    e.currentTarget.style.margin = "-2px -6px";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderRadius = "0";
                    e.currentTarget.style.padding = "0";
                    e.currentTarget.style.margin = "0";
                  }}
                >
                  {beatsPerBar}
                </span>
              )}
            </div>

            {/* Note value (bottom number) */}
            <div
              className="time-signature-text"
              style={{
                fontWeight: "600",
                color: "var(--text-primary)",
                lineHeight: "1",
                position: "absolute",
                top: "60%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              {isEditingNoteValue ? (
                <input
                  type="text"
                  inputMode="numeric"
                  value={noteValueInputValue}
                  onChange={handleNoteValueInputChange}
                  onBlur={handleNoteValueInputBlur}
                  onFocus={handleNoteValueInputFocus}
                  onKeyDown={handleNoteValueInputKeyDown}
                  autoFocus
                  autoComplete="off"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--text-primary)",
                    fontWeight: "600",
                    textAlign: "center",
                    outline: "none",
                    caretColor: "var(--text-primary)",
                    fontFamily: "inherit",
                  }}
                  className="time-signature-input"
                />
              ) : (
                <span
                  onClick={handleNoteValueClick}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease-out",
                    display: "inline-block",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--button-hover-bg)";
                    e.currentTarget.style.borderRadius = "10px";
                    e.currentTarget.style.padding = "2px 6px";
                    e.currentTarget.style.margin = "-2px -6px";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderRadius = "0";
                    e.currentTarget.style.padding = "0";
                    e.currentTarget.style.margin = "0";
                  }}
                >
                  {noteValue}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Two plus buttons stacked vertically */}
        <div
          className={`time-signature-buttons ${
            !controlsLocked ? "visible" : ""
          }`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Top plus button - for beats */}
          <CircularButton
            onClick={handleBeatsIncrease}
            disabled={beatsPerBar >= 16}
            size={44}
            tooltip="Increase beats per bar"
            tooltipShortcut="Ctrl + ↑"
          >
            {FaPlus({ size: 12 })}
          </CircularButton>

          {/* Bottom plus button - for note value */}
          <CircularButton
            onClick={handleNoteValueIncrease}
            disabled={noteValue >= 16}
            size={44}
            tooltip="Increase note value"
            tooltipShortcut="Ctrl + Shift + ↑"
          >
            {FaPlus({ size: 12 })}
          </CircularButton>
        </div>
      </div>
    </div>
  );
};
